/**
 * SellerVerificationPage - Apply for seller verification badge
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  ShieldCheck, Building, CheckCircle, Upload, User,
  FileText, Camera, Phone, MapPin, Loader2, Award,
  Clock, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge Card Component
const BadgeCard = ({ badge, onSelect }) => (
  <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => onSelect(badge.type)}>
    <CardContent className="p-6 text-center">
      <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
        badge.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'
      }`}>
        {badge.type === 'individual' ? (
          <ShieldCheck className={`w-8 h-8 ${badge.color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`} />
        ) : (
          <Building className={`w-8 h-8 ${badge.color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`} />
        )}
      </div>
      
      <h3 className="text-xl font-bold">{badge.name}</h3>
      
      <div className="mt-4 text-left">
        <p className="text-sm font-medium text-gray-700 mb-2">Benefits:</p>
        <ul className="text-sm text-gray-600 space-y-1">
          {badge.benefits.slice(0, 4).map((b, i) => (
            <li key={i} className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <Button className="w-full mt-6 group-hover:bg-blue-600">
        Apply Now
      </Button>
    </CardContent>
  </Card>
);

// Status Display Component
const VerificationStatusDisplay = ({ status, onReapply }) => {
  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50', text: 'Pending Review' },
    under_review: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-50', text: 'Under Review', animate: true },
    verified: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', text: 'Verified' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', text: 'Rejected' }
  };
  
  const config = statusConfig[status.status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <Card className={`${config.bg} border-2`}>
      <CardContent className="p-8 text-center">
        <Icon className={`w-16 h-16 mx-auto mb-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
        
        <h2 className="text-2xl font-bold">{config.text}</h2>
        
        {status.type && (
          <Badge className="mt-2">
            {status.type === 'individual' ? 'Individual Verification' : 'Business Verification'}
          </Badge>
        )}
        
        {status.verified_name && (
          <p className="text-gray-600 mt-2">Verified as: {status.verified_name}</p>
        )}
        
        {status.submitted_at && (
          <p className="text-sm text-gray-500 mt-4">
            Submitted: {new Date(status.submitted_at).toLocaleDateString()}
          </p>
        )}
        
        {status.verification_steps && (
          <div className="mt-6 text-left max-w-sm mx-auto">
            <p className="text-sm font-medium mb-2">Verification Progress:</p>
            {Object.entries(status.verification_steps).map(([step, completed]) => (
              <div key={step} className="flex items-center gap-2 text-sm">
                {completed ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <div className="w-4 h-4 border-2 border-gray-300 rounded-full" />
                )}
                <span className="capitalize">{step.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
        
        {status.status === 'rejected' && (
          <Button onClick={onReapply} className="mt-6">
            Reapply for Verification
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default function SellerVerificationPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [email, setEmail] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Individual form
  const [individualForm, setIndividualForm] = useState({
    full_legal_name: '',
    phone_number: '',
    address: '',
    id_type: 'nic',
    id_number: ''
  });
  
  // Business form
  const [businessForm, setBusinessForm] = useState({
    business_name: '',
    business_type: 'sole_proprietorship',
    registration_number: '',
    tax_id: '',
    business_address: '',
    contact_person: '',
    phone_number: ''
  });
  
  // Fetch badges info
  useEffect(() => {
    fetchBadges();
  }, []);
  
  const fetchBadges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/verification/badges`);
      const data = await response.json();
      setBadges(data.badges || []);
    } catch (err) {
      console.error('Failed to fetch badges:', err);
    }
  };
  
  const checkStatus = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/verification/status/${email}?country=${country}`);
      const data = await response.json();
      setVerificationStatus(data);
    } catch (err) {
      toast.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };
  
  const submitIndividualVerification = async () => {
    if (!individualForm.full_legal_name || !individualForm.id_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/verification/individual/submit?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_email: email,
          seller_name: email.split('@')[0],
          ...individualForm
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }
      
      toast.success('Verification request submitted!');
      setActiveForm(null);
      checkStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const submitBusinessVerification = async () => {
    if (!businessForm.business_name || !businessForm.registration_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/verification/business/submit?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_email: email,
          ...businessForm
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }
      
      toast.success('Business verification request submitted!');
      setActiveForm(null);
      checkStatus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-white/20 rounded-xl">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Seller Verification</h1>
              <p className="text-white/80">Get verified and build trust with buyers</p>
            </div>
          </div>
          
          {/* Email Input */}
          <div className="flex gap-3 max-w-md">
            <Input
              type="email"
              placeholder="Enter your seller email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900"
              onKeyDown={(e) => e.key === 'Enter' && checkStatus()}
            />
            <Button onClick={checkStatus} className="bg-white text-indigo-600 hover:bg-gray-100">
              Check Status
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : !searched ? (
          <div className="text-center py-8">
            <ShieldCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Enter Your Email</h2>
            <p className="text-gray-500 mb-8">Check your verification status or apply for a badge</p>
            
            {/* Show badge options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {badges.map(badge => (
                <BadgeCard 
                  key={badge.type} 
                  badge={badge} 
                  onSelect={(type) => {
                    if (!email) {
                      toast.error('Please enter your email first');
                      return;
                    }
                    setActiveForm(type);
                  }}
                />
              ))}
            </div>
          </div>
        ) : verificationStatus?.verified ? (
          <VerificationStatusDisplay status={verificationStatus} />
        ) : verificationStatus?.status ? (
          <VerificationStatusDisplay 
            status={verificationStatus} 
            onReapply={() => setVerificationStatus(null)}
          />
        ) : activeForm ? (
          // Show verification form
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {activeForm === 'individual' ? (
                  <>
                    <ShieldCheck className="w-5 h-5 text-blue-500" />
                    Individual Verification
                  </>
                ) : (
                  <>
                    <Building className="w-5 h-5 text-purple-500" />
                    Business Verification
                  </>
                )}
              </CardTitle>
              <CardDescription>
                Complete the form below to apply for verification
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeForm === 'individual' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Full Legal Name *</Label>
                      <Input
                        placeholder="As shown on ID"
                        value={individualForm.full_legal_name}
                        onChange={(e) => setIndividualForm({...individualForm, full_legal_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <Input
                        placeholder={country === "UK" ? "+44 7XXX XXXXXX" : "+94 XX XXX XXXX"}
                        value={individualForm.phone_number}
                        onChange={(e) => setIndividualForm({...individualForm, phone_number: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>ID Type *</Label>
                      <Select 
                        value={individualForm.id_type} 
                        onValueChange={(v) => setIndividualForm({...individualForm, id_type: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nic">National ID Card</SelectItem>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="driving_license">Driving License</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>ID Number *</Label>
                      <Input
                        placeholder="Enter ID number"
                        value={individualForm.id_number}
                        onChange={(e) => setIndividualForm({...individualForm, id_number: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Address *</Label>
                      <Textarea
                        placeholder="Your full address"
                        value={individualForm.address}
                        onChange={(e) => setIndividualForm({...individualForm, address: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <Alert>
                    <AlertTriangle className="w-4 h-4" />
                    <AlertDescription>
                      You may be asked to upload ID documents and a selfie for verification.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setActiveForm(null)}>
                      Cancel
                    </Button>
                    <Button onClick={submitIndividualVerification} disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Business Name *</Label>
                      <Input
                        placeholder="Registered business name"
                        value={businessForm.business_name}
                        onChange={(e) => setBusinessForm({...businessForm, business_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Business Type *</Label>
                      <Select 
                        value={businessForm.business_type} 
                        onValueChange={(v) => setBusinessForm({...businessForm, business_type: v})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="company">Private Limited Company</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Registration Number *</Label>
                      <Input
                        placeholder="Business registration number"
                        value={businessForm.registration_number}
                        onChange={(e) => setBusinessForm({...businessForm, registration_number: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Tax ID (if applicable)</Label>
                      <Input
                        placeholder="Tax registration number"
                        value={businessForm.tax_id}
                        onChange={(e) => setBusinessForm({...businessForm, tax_id: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Contact Person *</Label>
                      <Input
                        placeholder="Primary contact name"
                        value={businessForm.contact_person}
                        onChange={(e) => setBusinessForm({...businessForm, contact_person: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <Input
                        placeholder={country === "UK" ? "+44 7XXX XXXXXX" : "+94 XX XXX XXXX"}
                        value={businessForm.phone_number}
                        onChange={(e) => setBusinessForm({...businessForm, phone_number: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Business Address *</Label>
                      <Textarea
                        placeholder="Registered business address"
                        value={businessForm.business_address}
                        onChange={(e) => setBusinessForm({...businessForm, business_address: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <Alert>
                    <FileText className="w-4 h-4" />
                    <AlertDescription>
                      You may be asked to upload business registration certificate and tax documents.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setActiveForm(null)}>
                      Cancel
                    </Button>
                    <Button onClick={submitBusinessVerification} disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          // No verification - show options
          <div>
            <Alert className="mb-6">
              <AlertDescription>
                No verification request found for <strong>{email}</strong>. Choose a verification type below.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {badges.map(badge => (
                <BadgeCard 
                  key={badge.type} 
                  badge={badge} 
                  onSelect={setActiveForm}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
